import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { mockData } from '../utils/mockData';
import { Mail, Phone, MapPin, Instagram, Linkedin } from 'lucide-react';

// Product slugs mapping
const productSlugs = {
  'Labels': 'branding-labels',
  'Etiketler': 'branding-labels',
  'Hang Tags': 'hang-tags',
  'Karton Etiketler': 'hang-tags',
  'Patches': 'care-composition',
  'Yamalar': 'care-composition',
  'Packaging': 'packaging',
  'Ambalaj': 'packaging'
};

const Footer = () => {
  const { language } = useLanguage();
  const content = mockData[language];

  return (
    <footer id="contact" data-testid="site-footer" className="bg-gray-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Company Info */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold">ETITEKS</h3>
            <p className="text-gray-400">{content.footer.description}</p>
            <Link
              to="/contact"
              data-testid="footer-get-quote"
              className="inline-block text-base font-medium text-teal-400 hover:text-teal-300 transition-colors"
            >
              {language === 'tr' 
                ? '24 saat içinde teklif alın — aynı gün yanıt veriyoruz →' 
                : 'Get a quote within 24 hours — we respond same day →'}
            </Link>
            <div className="flex space-x-4 pt-2">
              <a 
                href="https://www.instagram.com/etitekslabel/" 
                data-testid="footer-instagram"
                aria-label="Etiteks on Instagram"
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="https://www.linkedin.com/company/etitekslabel/" 
                data-testid="footer-linkedin"
                aria-label="Etiteks on LinkedIn"
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
              >
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold mb-4">{content.nav.products}</h4>
            <ul className="space-y-2">
              {content.products.items.map((product, index) => {
                const slug = productSlugs[product.title];
                return (
                  <li key={index}>
                    <Link 
                      to={`/products/${slug}`} 
                      data-testid={`footer-product-${slug}`}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {product.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold mb-4">{content.nav.contact}</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin size={20} className="text-white mt-1" />
                <span data-testid="footer-address" className="text-gray-400">{content.footer.address}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail size={20} className="text-white" />
                <a data-testid="footer-email" href={`mailto:${content.footer.email}`} className="text-gray-400 hover:text-white transition-colors">
                  {content.footer.email}
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Phone size={20} className="text-white" />
                <a data-testid="footer-phone" href={`tel:${content.footer.phone}`} className="text-gray-400 hover:text-white transition-colors">
                  {content.footer.phone}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
          <p data-testid="footer-copyright">{content.footer.copyright}</p>
          <Link to="/faq" data-testid="footer-faq" className="inline-block mt-4 hover:text-white transition-colors">
            FAQ
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;